/**
 * ThreadList Component
 *
 * Middle pane showing email threads with virtualized infinite scroll.
 */

import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import type { EmailThreadPreview, EmailFolder, EmailDraft } from '@/services/emailApi';
import { formatEmailDate } from '@/utils/dateFormat';
import type { ThreadFilterMode } from './hooks/useEmailData';

// Priority color mapping for AI summaries
const PRIORITY_COLORS: Record<number, { bg: string; text: string; border: string }> = {
  5: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  4: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  3: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  2: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  1: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' },
};

// Category color mapping
const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  urgent: { bg: 'bg-red-100', text: 'text-red-700' },
  action_required: { bg: 'bg-orange-100', text: 'text-orange-700' },
  question: { bg: 'bg-blue-100', text: 'text-blue-700' },
  fyi: { bg: 'bg-gray-100', text: 'text-gray-600' },
  meeting: { bg: 'bg-purple-100', text: 'text-purple-700' },
  follow_up: { bg: 'bg-amber-100', text: 'text-amber-700' },
  approval: { bg: 'bg-green-100', text: 'text-green-700' },
  notification: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  default: { bg: 'bg-purple-100', text: 'text-purple-700' },
};

// Re-export type from hook for convenience
export type { ThreadFilterMode } from './hooks/useEmailData';

interface ThreadListProps {
  threads: EmailThreadPreview[];
  folders: EmailFolder[];
  selectedFolderId: string | null;
  selectedThreadId?: string | null;
  selectedDraftId?: string | null;
  totalCount: number;
  hasNextPage: boolean;
  loading: boolean;
  isLoadingMore: boolean;
  showReading: boolean;
  showDraftsView: boolean;
  drafts: EmailDraft[];
  loadingDrafts: boolean;
  filterMode: ThreadFilterMode;
  onFilterChange: (filter: ThreadFilterMode) => void;
  onSelectThread: (thread: EmailThreadPreview) => void;
  onLoadMore: () => Promise<void>;
  onEditDraft: (draft: EmailDraft) => void;
  onDeleteDraft: (draftId: string) => void;
  getUnreadCount: (folderId?: string) => number;
}

const ThreadList: React.FC<ThreadListProps> = ({
  threads,
  folders,
  selectedFolderId,
  selectedThreadId,
  selectedDraftId,
  totalCount,
  hasNextPage,
  loading,
  isLoadingMore,
  showReading,
  showDraftsView,
  drafts,
  loadingDrafts,
  filterMode,
  onFilterChange,
  onSelectThread,
  onLoadMore,
  onEditDraft,
  onDeleteDraft,
  getUnreadCount,
}) => {
  const { t } = useTranslation('email');
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState(600);
  const infiniteLoaderRef = useRef<InfiniteLoader>(null);
  const [hoveredThreadId, setHoveredThreadId] = useState<string | null>(null);
  const [aiModeEnabled, setAiModeEnabled] = useState(true); // AI mode ON by default
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Update list height on resize
  useEffect(() => {
    const updateHeight = () => {
      if (listContainerRef.current) {
        setListHeight(listContainerRef.current.clientHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Reset infinite loader cache when threads change
  useEffect(() => {
    if (infiniteLoaderRef.current) {
      infiniteLoaderRef.current.resetloadMoreItemsCache();
    }
  }, [threads.length === 0]);

  const folderName = folders.find(f => f.id === selectedFolderId)?.name || t('folders.all_mail');

  const filterLabels = {
    all: t('filters.all'),
    unread: t('filters.unread'),
    actionable: t('filters.actionable'),
  };

  return (
    <div className={`
      ${showReading ? 'hidden md:flex' : 'flex'}
      flex-1 md:flex-none md:w-96 bg-white border-r border-gray-200 flex-col overflow-hidden
    `}>
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {showDraftsView ? t('folders.drafts') : folderName}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {showDraftsView
                ? `${drafts.length} ${drafts.length === 1 ? t('ai.draft') : t('folders.drafts').toLowerCase()}`
                : `${totalCount} ${totalCount === 1 ? t('thread.conversation') : t('thread.conversations')}${getUnreadCount(selectedFolderId || undefined) > 0 ? ` ${getUnreadCount(selectedFolderId || undefined)} ${t('thread.unread')}` : ''}`
              }
            </p>
          </div>
          {/* Filter + AI Mode Toggle */}
          {!showDraftsView && (
            <div className="flex items-center gap-1">
              {/* Filter Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition ${
                    filterMode !== 'all'
                      ? 'text-blue-700 bg-blue-100'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                  title={t('filters.filter_emails')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  {filterLabels[filterMode]}
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown menu */}
                {showFilterDropdown && (
                  <>
                    {/* Backdrop to close dropdown */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowFilterDropdown(false)}
                    />
                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                      <button
                        onClick={() => { onFilterChange('all'); setShowFilterDropdown(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                          filterMode === 'all' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        {t('dashboard.all_emails')}
                      </button>
                      <button
                        onClick={() => { onFilterChange('unread'); setShowFilterDropdown(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                          filterMode === 'unread' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                        }`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                        </div>
                        {t('filters.unread')}
                      </button>
                      <button
                        onClick={() => { onFilterChange('actionable'); setShowFilterDropdown(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                          filterMode === 'actionable' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                        }`}
                      >
                        <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        {t('filters.actionable')}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* AI Mode Toggle */}
              <button
                onClick={() => setAiModeEnabled(!aiModeEnabled)}
                className={`p-2 rounded-lg transition ${
                  aiModeEnabled ? 'text-purple-600 bg-purple-100' : 'text-gray-400 hover:bg-gray-200'
                }`}
                title={aiModeEnabled ? t('filters.switch_to_classic') : t('filters.switch_to_ai')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      <div ref={listContainerRef} className="flex-1 overflow-hidden">
        {/* Drafts View */}
        {showDraftsView ? (
          loadingDrafts ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : drafts.length === 0 ? (
            <div className="p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-sm font-medium text-gray-900 mb-1">{t('folders.no_draft_emails')}</h3>
              <p className="text-xs text-gray-500">{t('compose.write_message')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 overflow-y-auto h-full">
              {drafts.map((draft) => (
                <div
                  key={draft.draft_id}
                  className={`p-3 cursor-pointer hover:bg-gray-50 transition ${
                    selectedDraftId === draft.draft_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                  onClick={() => onEditDraft(draft)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {draft.to && draft.to.length > 0
                            ? (typeof draft.to[0] === 'string' ? draft.to[0] : draft.to[0].email)
                            : t('common.no_recipients')}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                          {t('ai.draft')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 truncate">
                        {draft.subject || t('common.no_subject')}
                      </p>
                      {draft.body_html && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {draft.body_html.replace(/<[^>]*>/g, '').substring(0, 100)}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-xs text-gray-400">
                        {draft.updated_at ? formatEmailDate(draft.updated_at) : ''}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteDraft(draft.draft_id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 transition"
                        title={t('compose.delete_draft')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : threads.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="text-sm font-medium text-gray-900 mb-1">{t('folders.no_emails')}</h3>
            <p className="text-xs text-gray-500">{t('folders.folder_empty')}</p>
          </div>
        ) : (
          <InfiniteLoader
            ref={infiniteLoaderRef}
            isItemLoaded={(index) => index < threads.length}
            itemCount={hasNextPage ? threads.length + 1 : threads.length}
            loadMoreItems={onLoadMore}
            threshold={5}
          >
            {({ onItemsRendered, ref }) => (
              <List
                height={listHeight}
                itemCount={hasNextPage ? threads.length + 1 : threads.length}
                itemSize={100}
                width="100%"
                onItemsRendered={onItemsRendered}
                ref={ref}
                itemData={{
                  threads,
                  handleSelectThread: onSelectThread,
                  isLoadingMore,
                  hasNextPage,
                  selectedThreadId,
                  setHoveredThreadId,
                  hoveredThreadId,
                  aiModeEnabled,
                }}
              >
                {ThreadRow}
              </List>
            )}
          </InfiniteLoader>
        )}
      </div>
    </div>
  );
};

// Thread row component for virtualized list
interface ThreadRowData {
  threads: EmailThreadPreview[];
  handleSelectThread: (thread: EmailThreadPreview) => void;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  selectedThreadId: string | null | undefined;
  setHoveredThreadId: (id: string | null) => void;
  hoveredThreadId: string | null;
  aiModeEnabled: boolean;
}

const ThreadRow = ({ index, style, data }: ListChildComponentProps<ThreadRowData>) => {
  const { t } = useTranslation('email');

  // Show loading indicator for the extra item at the end
  if (index >= data.threads.length) {
    return (
      <div style={style} className="flex items-center justify-center py-4">
        {data.isLoadingMore && (
          <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" />
        )}
      </div>
    );
  }

  const thread = data.threads[index];
  const isSelected = data.selectedThreadId === thread.id;
  const isHovered = data.hoveredThreadId === thread.id;
  const hasAI = !!thread.ai_summary;
  const aiMode = data.aiModeEnabled;

  // Threads without AI processing are treated as high priority with all markers
  const priority = hasAI ? (thread.ai_summary?.priority_score || 3) : 5;
  const priorityColors = PRIORITY_COLORS[Math.min(5, Math.max(1, priority))];
  const categoryKey = hasAI ? (thread.ai_summary?.category || 'default').toLowerCase().replace(/\s+/g, '_') : 'urgent';
  const categoryColors = CATEGORY_COLORS[categoryKey] || CATEGORY_COLORS.default;
  const isHighPriority = priority >= 4;

  // For threads without AI: assume all markers are true (needs processing)
  const requiresResponse = hasAI ? thread.ai_summary?.requires_response : true;
  const hasActionItems = hasAI ? thread.ai_summary?.has_action_items : true;
  const isMeetingRequest = hasAI ? thread.ai_summary?.is_meeting_request : false;

  // AI mode: use priority-based background colors
  const getBackgroundClass = () => {
    if (isSelected) return 'bg-blue-50 border-l-blue-600';
    if (aiMode) {
      // No AI = treat as highest priority (needs processing)
      if (!hasAI) return 'bg-purple-50 border-l-purple-500 hover:bg-purple-100';
      // Priority-based coloring in AI mode
      if (priority >= 5) return 'bg-red-50 border-l-red-500 hover:bg-red-100';
      if (priority >= 4) return 'bg-orange-50 border-l-orange-400 hover:bg-orange-100';
      if (priority >= 3) return thread.unread_count > 0 ? 'bg-yellow-50/50 border-l-yellow-400 hover:bg-yellow-50' : 'bg-white border-l-transparent hover:bg-gray-50';
      return 'bg-white border-l-transparent hover:bg-gray-50';
    }
    // Classic mode
    if (thread.unread_count > 0) {
      return isHighPriority ? 'bg-red-50/50 border-l-red-400 hover:bg-red-50' : 'bg-blue-50/30 border-l-blue-400 hover:bg-blue-50';
    }
    return 'bg-white border-l-transparent hover:bg-gray-50';
  };

  return (
    <div
      style={style}
      onClick={() => data.handleSelectThread(thread)}
      onMouseEnter={() => data.setHoveredThreadId(thread.id)}
      onMouseLeave={() => data.setHoveredThreadId(null)}
      className={`px-3 ${aiMode ? 'py-2' : 'py-3'} cursor-pointer transition border-l-4 border-b border-gray-100 relative ${getBackgroundClass()}`}
    >
      <div className="flex items-start gap-2">
        {/* Left indicator - unread dot (both modes) */}
        <div className="flex-shrink-0 mt-1 flex flex-col items-center gap-1">
          {thread.unread_count > 0 ? (
            <div className={`w-2.5 h-2.5 rounded-full ${isHighPriority ? 'bg-red-500' : 'bg-blue-600'}`} />
          ) : (
            <div className="w-2.5 h-2.5" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Header row: sender + date */}
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`text-sm truncate ${thread.unread_count > 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                {thread.sender_name || thread.sender_email || 'Unknown'}
              </span>
              {thread.message_count > 1 && (
                <span className="text-xs text-gray-400 flex-shrink-0 bg-gray-100 px-1.5 rounded">
                  {thread.message_count}
                </span>
              )}
            </div>
            <span className={`text-xs whitespace-nowrap flex-shrink-0 ${thread.unread_count > 0 ? 'font-medium text-gray-700' : 'text-gray-500'}`}>
              {formatEmailDate(thread.last_message_at)}
            </span>
          </div>

          {/* AI Mode: Show AI summary instead of subject/snippet */}
          {aiMode && hasAI ? (
            <>
              {/* AI one-liner or summary - compact */}
              <div className={`text-xs leading-snug mb-1 line-clamp-2 ${thread.unread_count > 0 ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                {thread.ai_summary?.one_liner || thread.ai_summary?.summary || thread.subject || t('common.no_subject')}
              </div>

              {/* AI indicators row - compact chips */}
              <div className="flex items-center gap-1 flex-wrap">
                {/* Meeting request chip - prominent */}
                {isMeetingRequest && (
                  <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded font-medium" title={t('ai.meeting_request_title')}>
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {t('ai.meeting_request')}
                  </span>
                )}

                {/* Category badge */}
                <span className={`text-[9px] px-1 py-0.5 rounded ${categoryColors.bg} ${categoryColors.text}`}>
                  {thread.ai_summary?.category || t('ai.email_category')}
                </span>

                {/* Response needed icon */}
                {requiresResponse && (
                  <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 bg-amber-100 text-amber-700 rounded" title={t('ai.response_needed')}>
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    {t('ai.reply')}
                  </span>
                )}

                {/* Action items indicator */}
                {hasActionItems && (
                  <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 bg-red-100 text-red-700 rounded" title={t('ai.has_action_items')}>
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    {t('ai.tasks')}
                  </span>
                )}

                {/* Sentiment indicator */}
                {thread.ai_summary?.sentiment && thread.ai_summary.sentiment !== 'neutral' && (
                  <span className={`text-[9px] px-1 py-0.5 rounded ${
                    thread.ai_summary.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
                    thread.ai_summary.sentiment === 'negative' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-600'
                  }`} title={`${thread.ai_summary.sentiment} ${t('ai.sentiment')}`}>
                    {thread.ai_summary.sentiment === 'positive' ? '😊' : '😟'}
                  </span>
                )}
              </div>
            </>
          ) : aiMode && !hasAI ? (
            <>
              {/* AI Mode but no AI processing - show "awaiting AI" with original content */}
              <div className="flex items-center gap-1.5 mb-1">
                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">
                  <svg className="w-3 h-3 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  {t('ai.awaiting_analysis')}
                </span>
              </div>

              {/* Show original content in muted style */}
              <div className="pl-2 border-l-2 border-purple-200">
                <div className={`text-xs mb-0.5 truncate ${thread.unread_count > 0 ? 'font-medium text-gray-700' : 'text-gray-500'}`}>
                  📧 {thread.subject || t('common.no_subject')}
                </div>
                <div className="text-[11px] text-gray-400 line-clamp-1">
                  {thread.snippet}
                </div>
              </div>

              {/* Potential action indicators */}
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 bg-amber-50 text-amber-600 rounded" title={t('ai.may_need_response')}>
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  {t('ai.reply_question')}
                </span>
                <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 bg-red-50 text-red-500 rounded" title={t('ai.may_have_tasks')}>
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  {t('ai.tasks_question')}
                </span>
              </div>
            </>
          ) : (
            <>
              {/* Classic mode: subject + snippet */}
              <div className={`text-sm mb-0.5 truncate ${thread.unread_count > 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                {thread.subject || t('common.no_subject')}
              </div>

              <div className="text-xs text-gray-500 line-clamp-1 mb-1">
                {thread.snippet}
              </div>

              {/* AI indicators row (classic mode) */}
              {hasAI && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {priority >= 4 && (
                    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${priorityColors.bg} ${priorityColors.text} font-medium`}>
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {priority === 5 ? t('ai.urgent') : t('ai.high')}
                    </span>
                  )}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${categoryColors.bg} ${categoryColors.text}`}>
                    {thread.ai_summary?.category || t('ai.email_category')}
                  </span>
                  {thread.ai_summary?.requires_response && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                      {t('ai.reply')}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Right icons + priority (AI mode) */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0 pt-0.5">
          {/* Top: attachment + star */}
          <div className="flex items-center gap-1">
            {thread.has_attachments && (
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            )}
            {thread.is_starred && (
              <svg className="w-4 h-4 text-yellow-500 fill-yellow-500" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            )}
          </div>
          {/* Bottom: priority chip (AI mode only) */}
          {aiMode && hasAI && (
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${priorityColors.bg} ${priorityColors.text}`} title={t('ai.priority_score', { score: priority })}>
              {priority >= 4 ? (
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              ) : (
                priority
              )}
            </div>
          )}
          {/* No AI yet indicator */}
          {aiMode && !hasAI && (
            <div className="w-5 h-5 rounded-full flex items-center justify-center bg-purple-100 text-purple-700" title={t('ai.awaiting_ai_analysis')}>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Hover tooltip with AI summary - only in classic mode */}
      {!aiMode && isHovered && hasAI && thread.ai_summary && (
        <div className="absolute left-full top-0 ml-2 z-50 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-4 pointer-events-none">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityColors.bg} ${priorityColors.text}`}>
              {t('ai.priority_score', { score: priority })}
            </span>
            <span className={`px-2 py-0.5 rounded text-xs ${categoryColors.bg} ${categoryColors.text}`}>
              {thread.ai_summary.category}
            </span>
          </div>

          {thread.ai_summary.one_liner && (
            <p className="text-sm font-medium text-gray-900 mb-2">
              {thread.ai_summary.one_liner}
            </p>
          )}

          {thread.ai_summary.summary && (
            <p className="text-xs text-gray-600 mb-3 line-clamp-3">
              {thread.ai_summary.summary}
            </p>
          )}

          <div className="flex flex-wrap gap-2 text-[10px]">
            {thread.ai_summary.requires_response && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                {t('ai.response_needed')}
              </span>
            )}
            {thread.ai_summary.sentiment && (
              <span className={`px-1.5 py-0.5 rounded ${
                thread.ai_summary.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
                thread.ai_summary.sentiment === 'negative' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {thread.ai_summary.sentiment === 'positive' ? t('ai.positive_tone') : thread.ai_summary.sentiment === 'negative' ? t('ai.negative_tone') : t('ai.neutral_tone')}
              </span>
            )}
          </div>

          {/* Tooltip arrow */}
          <div className="absolute left-0 top-4 -translate-x-1 w-2 h-2 bg-white border-l border-b border-gray-200 transform -rotate-45"></div>
        </div>
      )}
    </div>
  );
};

export default ThreadList;
